import {
  type CompetencyCode,
  type KnowledgeCode,
  type LegacyQuestion,
  type MisconceptionCode,
  type Question,
} from '../types'
import { logicQuestions } from './logic_questions'
import { analysisQuestions } from './analysis_questions'
import { authoredDataVariants } from './data_authored_variants'
import { authoredLogicVariants } from './logic_authored_variants'
import { authoredMathVariants } from './math_authored_variants'
import hsaQuantitativeOriginal from '../../../../content/hsa-quantitative-original.json'
import logicReviewedOriginal from '../../../../content/logic-reviewed-original.json'
import tsaSequencesOriginal from '../../../../content/tsa-sequences-original.json'
import tsaCh1FunctionsOriginal from '../../../../content/tsa-ch1-functions-source-linked-draft.json'

import tsaMassTypstDraft from '../../../../content/tsa-mass-typst-source-linked-draft.json'

const compact = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()

function inferKnowledge(question: LegacyQuestion): KnowledgeCode[] {
  let text = compact(`${question.id} ${question.domain} ${question.topic} ${question.tags?.join(' ')} ${question.aiContext || ''} ${question.stem || ''} ${question.solution || ''} ${question.hint1 || ''} ${question.hint2 || ''}`);
  
  if (question.knowledge?.length) {
    // Filter out broad legacy categories so they can fall through to the text-based checks below
    const filtered = question.knowledge.filter(k => (k as string) !== 'algebra-functions' && k !== 'algebra' && k !== 'geometry' && k !== 'spatial-geometry');
    if (filtered.length > 0) return filtered as KnowledgeCode[];
  }
  
  if (question.topic === 'phan-tich-so-lieu' || text.includes('phan-tich-so-lieu')) return ['data'];
  if (text.includes('logic') || text.includes('sap xep') || text.includes('quy luat')) return ['logic'];
  if (text.includes('du lieu') || text.includes('phan tich so lieu') || text.includes('chart')) return ['data'];
  if (text.includes('mo hinh')) return ['modelling'];
  if (text.includes('xac suat') || text.includes('thong ke') || text.includes('xacsuat') || text.includes('thxs') || text.includes('xs-pp') || text.includes('xác-suất') || text.includes('to hop') || text.includes('chinh hop') || text.includes('hoan vi') || text.includes('nhi thuc newton')) return ['probability-statistics'];
  
  if (text.includes('so hoc')) return ['arithmetic'];
  
  // 1. Spatial & Oxyz Geometry (Check before trig/planar because 3D has triangles)
  if (text.includes('oxyz') || text.includes('toa do trong khong gian') || (text.includes('toa do') && (text.includes('mat cau') || text.includes('mat phang')))) {
    return ['oxyz'];
  }
  if (text.includes('hinh') || text.includes('hhkg') || text.includes('geometry') || text.includes('mat cau') || text.includes('the tich') || text.includes('khoi chop') || text.includes('hinh tru') || text.includes('hinh non') || text.includes('hinh hop') || text.includes('mat tru') || text.includes('khong gian') || (question.knowledge && (question.knowledge.includes('geometry') || question.knowledge.includes('spatial-geometry')))) {
    if (text.includes('oxyz') || text.includes('toa do') || text.includes('toado')) return ['oxyz'];
    return ['spatial-geometry'];
  }

  // 2. Algebra & Calculus (These can contain sin/cos/triangles but belong to their respective fields)
  if (text.includes('day so') || text.includes('cap so') || text.includes('tang truong')) return ['sequences'];
  if (text.includes('ham so') || text.includes('do thi') || text.includes('tiem can') || text.includes('tang giam') || text.includes('cuc tri') || text.includes('don dieu') || text.includes('dao ham')) return ['functions'];
  if (text.includes('logarit') || /\blog\b/.test(text) || /\bmu\b/.test(text)) return ['exponents-logarithms'];
  if (text.includes('tich phan') || text.includes('nguyen ham') || text.includes('giai tich')) return ['calculus'];
  if (text.includes('so phuc')) return ['complex-numbers'];

  // 3. Planar Geometry & Trigonometry
  // Use regex \bsin\b | \bcos\b | \btan\b (wait, tan might match 'tấn' but data questions are filtered above)
  if (text.includes('luong giac') || /\b(sin|cos|tan|cot)\b/.test(text) || text.includes('tam giac') || text.includes('tamgiac') || text.includes('he thuc luong')) return ['trigonometry'];
  if (text.includes('dien tich') && text.includes('duong tron')) return ['planar-geometry'];
  if (text.includes('hinh vuong') || text.includes('hinh chu nhat') || text.includes('hinh thang') || text.includes('oxy')) return ['planar-geometry'];
  
  return ['algebra'];
}

function inferCompetencies(question: LegacyQuestion): CompetencyCode[] {
  const result = new Set<CompetencyCode>()
  for (const legacy of question.competency) {
    const text = compact(legacy)
    if (text.includes('tinh toan')) result.add('calculation')
    else if (text.includes('dinh luong')) result.add('quantitative-reasoning')
    else if (text.includes('logic')) result.add('logical-reasoning')
    else if (text.includes('du lieu')) result.add('data-analysis')
    else if (text.includes('mo hinh')) result.add('modelling')
    else if (text.includes('giai quyet')) result.add('problem-solving')
    else if (text.includes('quyet dinh')) result.add('decision-making')
    else if (text.includes('quy luat')) result.add('pattern-recognition')
  }

  const knowledge = inferKnowledge(question)[0]
  if (!result.size && knowledge === 'logic') result.add('logical-reasoning')
  if (!result.size && knowledge === 'data') result.add('data-analysis')
  if (!result.size) result.add('problem-solving')
  return [...result]
}

function inferMisconceptions(question: LegacyQuestion, knowledge: KnowledgeCode[]): MisconceptionCode[] {
  if (question.misconceptionOptions?.length) return question.misconceptionOptions
  if (knowledge.includes('logic')) {
    return ['condition-reversal', 'strategy-choice', 'careless-reading', 'time-pressure']
  }
  if (knowledge.includes('data')) {
    return ['data-reading', 'calculation-slip', 'careless-reading', 'time-pressure']
  }
  return ['concept-gap', 'calculation-slip', 'strategy-choice', 'time-pressure']
}

export function normalizeQuestion(question: LegacyQuestion): Question {
  const knowledge = inferKnowledge(question)
  return {
    ...question,
    knowledge,
    competency: inferCompetencies(question),
    // Do not pretend old, unreviewed questions are genuine parallel variants.
    variantGroup: question.variantGroup ?? question.id,
    prerequisites: question.prerequisites?.length ? question.prerequisites.flatMap(p => (p === 'geometry' || (p as string) === 'algebra-functions' || p === 'algebra') ? knowledge : [p]) as KnowledgeCode[] : knowledge,
    misconceptionOptions: inferMisconceptions(question, knowledge),
  }
}

/**
 * Original authored HSA quantitative questions live in content/ so they can be
 * editorially reviewed independently from UI code. They intentionally model
 * the public exam blueprint, not any copyrighted reference-test wording.
 */
const originalHsaQuestions = hsaQuantitativeOriginal as Question[]
const reviewedLogicQuestions = logicReviewedOriginal as Question[]
const tsaSequencesQuestions = tsaSequencesOriginal as Question[]
const tsaCh1FunctionsQuestions = tsaCh1FunctionsOriginal as Question[]


/**
 * These legacy graph prompts arrived with corrupted choices or unverified
 * generated explanations. Keep their historical IDs out of new sessions until
 * they are re-authored; existing attempts retain their stored topic metadata.
 */
const retiredLegacyQuestionIds = new Set([
  'logic_81', 'logic_82', 'logic_83',
  'logic_88', 'logic_89', 'logic_90', 'logic_91', 'logic_92',
])

export const questions: Question[] = [...logicQuestions, ...analysisQuestions, ...originalHsaQuestions, ...reviewedLogicQuestions, ...tsaSequencesQuestions, ...tsaCh1FunctionsQuestions, ...(tsaMassTypstDraft as Question[]), ...authoredLogicVariants, ...authoredDataVariants, ...authoredMathVariants]
  .filter(question => !retiredLegacyQuestionIds.has(question.id))
  .map(normalizeQuestion)
